import React from 'react';
import * as Styled from './styled';
import { PhoneButton } from 'components/phone-button';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import PhoneIcon from 'assets/svg/phone-new.svg';
import MailIcon from 'assets/svg/send.svg';
import { useLocale } from 'locale';
import { useSpring, animated } from 'react-spring';
import { TextField } from 'ds/components/input/TextField';
import { IContactAgentValues } from 'helpers/contactFormStore';
import { createMortgageValidationFn } from 'components/madad/contact-modal/utils';
import { SubmitContactAgentPayload, ContactAgentSubmitterSource } from 'store/state/app/payloads';
import { Poi } from 'utils/marketplaceRoutes';
import { ContactAgentModalSource } from 'store/state/app';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import CloseIcon from 'assets/svg/close_1.5.svg';
import { useABTests } from 'hooks/useABTests';
import { ListingPageV5 } from 'config/abTests';
import TermsLine from 'components/terms-line';

export interface ContactButtonsProps {
  phone: string;
  poi: Poi;
  isContacted: boolean;
  onShowPhone: (e: React.SyntheticEvent<HTMLElement>) => void;
  handleModalOpen: () => void;
  showPhone: boolean;
  dataAttributePrefix: string;
  contactValues: IContactAgentValues;
  handleSubmitEvent?: (hasErrors: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  onContactAgentInputClick: (eventName: string, poi: Poi) => void;
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => void;
  phoneButtonLabel?: string;
  mailButtonLabel?: string;
  equalWidthButtons?: boolean;
  source?: ContactAgentModalSource;
}

interface FormState {
  userName: string;
  userPhone: string;
  formErrors: {
    phone?: string;
    message?: string;
  };
}

export const ContactButtons: React.FC<ContactButtonsProps> = (props) => {
  const {
    dataAttributePrefix,
    handleModalOpen,
    isContacted,
    onShowPhone,
    showPhone,
    phone,
    contactValues,
    onContactAgentInputClick,
    onContactPageSubmit,
    poi,
    handleSubmitEvent,
    phoneButtonLabel,
    equalWidthButtons,
    mailButtonLabel,
    source = ContactAgentModalSource.ContactSticky,
  } = props;

  const { listingPageV5 } = useABTests();
  const [ isFormVisible, setIsFormVisible ] = React.useState(false);
  const { t } = useLocale();
  const [ state, setState ] = React.useState<FormState>({ userName: contactValues.name, userPhone: contactValues.phone, formErrors: {} });
  const isModeB = listingPageV5 === ListingPageV5.modeB && (poi.type === 'bulletin' || poi.type === 'commercialBulletin');

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, userPhone: e.currentTarget.value });
  const onMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, userName: e.currentTarget.value });

  const handleContactButtonClick = () => {
    if (isModeB) {
      if (isFormVisible) onSubmit();
      else setIsFormVisible(true);
    }
    else {
      handleModalOpen();
    }
  };

  const validate = React.useCallback(createMortgageValidationFn({
    phone: {
      invalid: t('contactAgentModal.form.phone.invalid'),
      required: t('contactAgentModal.form.phone.required'),
    },
    message: {
      required: t('contactAgentModal.form.message.required'),
    },
  }), [ t ]);

  const onSubmit = () => {
    const errors = validate({
      phone: state.userPhone,
    });
    const formValues = {
      phone: state.userPhone,
      name: state.userName,
      isMortgageConsult: false,
      type: poi.type,
      id: poi.id,
      source,
    };
    setState({ ...state, formErrors: errors });

    let hasErrors = false;

    if (errors.phone) {
      hasErrors = true;
    }

    handleSubmitEvent(hasErrors, formValues, source);

    if (hasErrors) return;

    setIsFormVisible(false);
    onContactPageSubmit({
      key: formValues.id,
      variables: formValues,
      source: ContactAgentSubmitterSource.Form,
    });
  };

  const phoneButtonStyles = useSpring({
    opacity: isFormVisible ? 0 : 1,
    config: { duration: 300 },
  });

  const phoneButtonHideStyled = useSpring({
    visibility: isFormVisible ? 'hidden' : 'visible',
    config: { delay: 300 },
  });

  const styles = useSpring({
    display: 'flex',
    position: 'absolute',
    opacity: isFormVisible ? 1 : 0,
    left: isFormVisible ? '228px' : '-454px',
    config: { duration: 300 },
    pointerEvents: isFormVisible ? 'auto' : 'none',
  });


  return (
    <Styled.Root equalWidthButtons={equalWidthButtons} className="contact-buttons" data-auto={`${dataAttributePrefix}-sticky`}>
      {phone ? (
        <animated.div style={{ ...phoneButtonStyles, ...phoneButtonHideStyled }}>
          <PhoneButton onClick={onShowPhone} href={createPhoneURI(phone)} data-auto={`${dataAttributePrefix}-button-sticky-text`} size="extraLarge">
            <PhoneIcon width={24} height={24} />
            {showPhone ? phone : phoneButtonLabel || t(isModeB ? 'contactAgentPhone.button.modeB' : 'contactAgentModal.callButton.desktop')}
          </PhoneButton>
        </animated.div>
      ) : null}
      <Styled.ContactButton disabled={isContacted} isInitialIconColor onClick={handleContactButtonClick} data-auto={isContacted ? `${dataAttributePrefix}-phone-button-modal-disabled` : `${dataAttributePrefix}-phone-button-modal`} size="extraLarge" mode="ghost">
        <MailIcon width={24} height={24} />
        {isContacted ? t('contactAgentModal.button.contacted.modeB') : (mailButtonLabel || t('contactAgentModal.button.modeB'))}
      </Styled.ContactButton>
      <animated.div style={styles}>
        <Styled.CloseIconWrap onClick={() => setIsFormVisible(false)}>
          <CloseIcon width={16} height={16} />
        </Styled.CloseIconWrap>
        <Styled.FormRoot>
          <Styled.Form>
            <TextField
              fullWidth
              data-auto="name-input"
              hasError={false}
              errorMessage={null}
              value={state.userName}
              onChange={onMessageChange}
              placeholder={t('uploadBulletinPage.contactsStep.userNameLabel')}
              onClick={() => onContactAgentInputClick('property_lead_form_messege_box_click', poi)}
            />
            <TextField
              fullWidth
              value={state.userPhone}
              hasError={Boolean(state.formErrors.phone)}
              data-auto="phone-input"
              errorMessage={state.formErrors.phone}
              onChange={onPhoneChange}
              placeholder={t('contactForm.contact.form.placeholder.phone')}
              onClick={() => onContactAgentInputClick('property_lead_form_phone_text_box_click', poi)}
            />
          </Styled.Form>
          <TermsLine />
        </Styled.FormRoot>
      </animated.div>
    </Styled.Root>
  );
};
